import React, { useState, useEffect } from 'react';
import { Table, Input, Select, Button, Tag, Spin, Empty, Row, Col, Card, Statistic, Tooltip } from 'antd';
import {
  SearchOutlined, CloseCircleOutlined, ExportOutlined, BellOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { trainingService } from '@iso/services';

const { Option } = Select;

const getStatusTag = (status) => {
  const map = {
    compliant: { className: 'status-compliant', label: 'Competent' },
    expiring_soon: { className: 'status-expiring', label: 'Expiring Soon' },
    expired: { className: 'status-expired', label: 'Expired' },
    not_competent: { className: 'status-missing', label: 'Not Competent' },
  };
  const s = map[status] || { className: 'status-missing', label: status || 'Unknown' };
  return <Tag className={s.className}>{s.label}</Tag>;
};

const getDaysDisplay = (days) => {
  if (days === null || days === undefined) return <span style={{ color: '#8c8c8c' }}>N/A</span>;
  if (days <= 0) return <span className="days-expired">{Math.abs(days)}d overdue</span>;
  if (days <= 7) return <span className="days-expired">{days}d</span>;
  if (days <= 30) return <span className="days-critical">{days}d</span>;
  if (days <= 90) return <span className="days-warning">{days}d</span>;
  return <span className="days-ok">{days}d</span>;
};

const ComplianceMonitoring = ({ propertyId }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ compliant: 0, expiring_soon: 0, expired: 0, not_competent: 0 });
  const [search, setSearch] = useState('');
  const [filterExpiry, setFilterExpiry] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPersonType, setFilterPersonType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    if (!propertyId) return;
    fetchStats();
    fetchRecords();
  }, [propertyId, currentPage, filterExpiry, filterStatus]);

  const fetchStats = async () => {
    const res = await trainingService.getComplianceStats(propertyId);
    if (res) setStats(res.compliance_stats || {});
  };

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage };
      if (filterExpiry) params.expiry_window = filterExpiry;
      if (filterStatus) params.status = filterStatus;
      const res = await trainingService.getComplianceData(propertyId, params);
      if (res) {
        setRecords(res.compliance_records || []);
        setTotalItems(res.total_item || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredRecords = records.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.person_name || '').toLowerCase().includes(q)
      || (r.training_name || '').toLowerCase().includes(q);
  }).filter(r => {
    if (!filterPersonType) return true;
    return r.person_type === filterPersonType;
  });

  const statCards = [
    { title: 'Expiring in 7 Days', value: records.filter(r => r.days_until_expiry !== null && r.days_until_expiry >= 0 && r.days_until_expiry <= 7).length, color: '#f5222d', bg: '#fff1f0' },
    { title: 'Expiring in 30 Days', value: stats.expiring_soon, color: '#faad14', bg: '#fffbe6' },
    { title: 'Expired Records', value: stats.expired, color: '#fa541c', bg: '#fff2e8' },
    { title: 'Not Competent', value: stats.not_competent, color: '#722ed1', bg: '#f9f0ff' },
  ];

  const columns = [
    {
      title: 'Person Name',
      dataIndex: 'person_name',
      key: 'person_name',
      render: t => <span style={{ fontWeight: 600 }}>{t}</span>,
    },
    {
      title: 'Type',
      dataIndex: 'person_type',
      key: 'person_type',
      render: t => <Tag>{t}</Tag>,
    },
    {
      title: 'Training',
      dataIndex: 'training_name',
      key: 'training_name',
    },
    {
      title: 'Expiry Date',
      dataIndex: 'expiry_date',
      key: 'expiry_date',
      render: d => d ? moment(d).format('DD/MM/YYYY') : '—',
    },
    {
      title: 'Days Until Expiry',
      dataIndex: 'days_until_expiry',
      key: 'days_until_expiry',
      sorter: (a, b) => (a.days_until_expiry ?? 9999) - (b.days_until_expiry ?? 9999),
      render: getDaysDisplay,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: getStatusTag,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Tooltip title="Notify feature coming soon">
          <Button
            size="small"
            icon={<BellOutlined />}
            disabled
            style={{ borderRadius: 6 }}
          >
            Notify
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="tab-content">
      {/* Stats Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {statCards.map((card, i) => (
          <Col xs={24} sm={12} lg={6} key={i}>
            <Card className="stat-card" style={{ cursor: 'pointer' }}>
              <Statistic
                title={card.title}
                value={card.value}
                valueStyle={{ color: card.color, fontSize: 28, fontWeight: 700 }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div className="filter-row">
          <Input
            className="search-bar training-search-bar"
            prefix={<SearchOutlined />}
            suffix={search ? <CloseCircleOutlined onClick={() => setSearch('')} /> : null}
            placeholder="Search by person or training..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <Select
            className="filter-select"
            value={filterExpiry}
            onChange={v => { setFilterExpiry(v); setCurrentPage(1); }}
            placeholder="Expiry Window"
          >
            <Option value="">All</Option>
            <Option value="7">Expiring in 7 Days</Option>
            <Option value="30">Expiring in 30 Days</Option>
          </Select>
          <Select
            className="filter-select"
            value={filterPersonType}
            onChange={v => { setFilterPersonType(v); setCurrentPage(1); }}
            placeholder="All Person Types"
          >
            <Option value="">All Person Types</Option>
            <Option value="staff">Staff</Option>
            <Option value="visitor">Visitor</Option>
            <Option value="contractor">Contractor</Option>
          </Select>
          <Select
            className="filter-select"
            value={filterStatus}
            onChange={v => { setFilterStatus(v); setCurrentPage(1); }}
            placeholder="All Statuses"
          >
            <Option value="">All Statuses</Option>
            <Option value="compliant">Competent</Option>
            <Option value="expiring_soon">Expiring Soon</Option>
            <Option value="expired">Expired</Option>
            <Option value="not_competent">Not Competent</Option>
          </Select>
        </div>
        <Tooltip title="Export feature coming soon">
          <Button icon={<ExportOutlined />} disabled style={{ borderRadius: 8 }}>
            Export Report
          </Button>
        </Tooltip>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
      ) : filteredRecords.length === 0 ? (
        <Empty description="No compliance records found" style={{ marginTop: 60 }} />
      ) : (
        <Table
          dataSource={filteredRecords}
          columns={columns}
          rowKey={(r, i) => `${r.person_id}_${r.training_asset_id}_${i}`}
          pagination={{
            current: currentPage,
            total: totalItems,
            onChange: setCurrentPage,
            showTotal: total => `${total} records`,
          }}
          scroll={{ x: 1000 }}
        />
      )}
    </div>
  );
};

export default ComplianceMonitoring;
